'use client';

import { PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

type NavigationType = {
    previousRoute: string | null;
    back: () => void;
};

const NavigationContext = createContext<NavigationType>({
    previousRoute: null,
    back: () => {},
});

export function NavigationContextProvider(props: PropsWithChildren) {
    const pathname = usePathname();
    const router = useRouter();
    const searchParams = useSearchParams();
    const [stack, setStack] = useState<string[]>([]);

    useEffect(() => {
        const url = `${pathname}${searchParams?.size ? `?${searchParams}` : ''}`;
        if (stack.length && stack[0] === url) {
            return;
        }
        setStack([url, ...stack]);
    }, [pathname, searchParams, stack]);

    const value = useMemo<NavigationType>(() => {
        const back = () => {
            setStack(stack.slice(2));
            router.back();
        };

        return {
            previousRoute: stack.length > 1 ? stack[1] : null,
            back,
        };
    }, [stack, router]);

    return <NavigationContext.Provider value={value}>{props.children}</NavigationContext.Provider>;
}

export default function useNavigationState() {
    const context = useContext(NavigationContext);

    if (!context) {
        throw new Error('You need to wrap NavigationContextProvider.');
    }

    return context;
}
